// Vendors
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useErrorBoundary } from 'react-error-boundary';
// Contexts
import { useAppContextConsumerHook } from 'contexts/app.context';
import { useLayoutContextConsumerHook } from 'components/layout/contexts/layout.context';
import { useContractContextConsumerHook } from 'containers/contract/contexts/contract.context';
// Event Handlers
import { ContractViewHandlers } from '../handlers/contract.view.handlers';
// Hooks
import { useAppTrackingHook } from 'tracking/app-tracking.hook';
// Types
import { ContractViewDataApiResponseType } from '../types/contract-view-data-api-response.type';
import { ContractConsentViewType, ContractViewHookType } from './types/contract.view.hook.types';
import { ContractViewTrackingHandlers } from '../handlers/contract.view.tracking.handlers';

const useContractHook = (): ContractViewHookType => {
  const [screenDetails, setScreenDetails] = useState<ContractViewDataApiResponseType>(
    {} as ContractViewDataApiResponseType
  );
  const [consents, setConsents] = useState<ContractConsentViewType[]>(
    [] as ContractConsentViewType[]
  );
  const { showBoundary: setError } = useErrorBoundary();

  const { setContractState, setOtpId } = useContractContextConsumerHook(),
    { setMainButtonConfiguration } = useLayoutContextConsumerHook(),
    { callbackUrl, loanId, setOnHeaderBackClick } = useAppContextConsumerHook(),
    { formatMessage } = useIntl();

  const {
    handleContinueButtonClickTracking,
    handleDownloadPdfButtonClickTracking,
    handleConsentsCheckboxChangeTracking,
  } = useAppTrackingHook(ContractViewTrackingHandlers);

  const {
    handleSetupContractButtonConfiguration,
    handleLoadScreenDetails,
    handleCheckConsent,
    handleUpdateContractHeaderBackClick,
  } = ContractViewHandlers({
    callbackUrl,
    consents,
    formatMessage,
    handleContinueButtonClickTracking,
    handleConsentsCheckboxChangeTracking,
    loanId,
    setMainButtonConfiguration,
    setScreenDetails,
    setContractState,
    setConsents,
    setOtpId,
    setError,
    setOnHeaderBackClick,
  });

  useEffect(() => {
    handleLoadScreenDetails();
    handleUpdateContractHeaderBackClick();

    return () => {
      setOnHeaderBackClick(null);
    };
  }, []);

  useEffect(() => {
    handleSetupContractButtonConfiguration();
  }, [consents]);

  const memorizedDetails = useMemo(() => screenDetails, [screenDetails]);

  return {
    screenDetails: memorizedDetails,
    consents,
    handleCheckConsent,
    handleDownloadPdfButtonClickTracking,
  };
};

export { useContractHook };
